import React, { useState, useCallback, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { ScrollArea } from './ui/scroll-area';
import { useSettings } from './SettingsContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Settings, Bell, BellOff, Loader2, Smartphone, AlertCircle, ChevronDown } from 'lucide-react';
import { useDebouncedCallback } from '../hooks/useDebounce';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { useNotificationPreferences } from '../hooks/useNotificationPreferences';
import { Switch } from './ui/switch';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { cn } from '../lib/utils';

type SettingsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTab?: 'appearance' | 'notifications';
};

/** Push notification settings sub-component */
function PushNotificationSettings() {
  const push = usePushNotifications();
  const prefs = useNotificationPreferences();
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const settingsError = push.error || prefs.error;

  const handleDeviceToggle = useCallback(async (checked: boolean) => {
    if (checked) {
      await push.subscribe();
    } else {
      await push.unsubscribe();
    }
  }, [push]);

  const handleCategoryToggle = useCallback(async (key: string, checked: boolean) => {
    await prefs.updateCategory(key, checked);
  }, [prefs]);

  // Not supported
  if (!push.isSupported) {
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-3 rounded-lg border border-border/50 bg-muted/30 p-4">
          <AlertCircle className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
          <div className="space-y-1">
            <p className="text-sm font-medium">Push notifications not supported</p>
            <p className="text-xs text-muted-foreground">
              Your browser doesn't support push notifications. Try using Chrome, Firefox, or Safari 16.4+.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Not configured (missing VAPID key)
  if (!push.isConfigured) {
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-3 rounded-lg border border-border/50 bg-muted/30 p-4">
          <AlertCircle className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
          <div className="space-y-1">
            <p className="text-sm font-medium">Push notifications not configured</p>
            <p className="text-xs text-muted-foreground">
              Push notifications have not been configured for this application.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Permission denied
  if (push.permissionState === 'denied') {
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4">
          <BellOff className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-destructive">Notifications blocked</p>
            <p className="text-xs text-muted-foreground">
              You've blocked notifications for this site. To enable them, update your browser's site settings.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {settingsError && (
        <div className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4">
          <AlertCircle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-destructive">Notification settings need attention</p>
            <p className="text-xs text-muted-foreground">{settingsError}</p>
          </div>
        </div>
      )}

      {/* Device Push Notifications Toggle */}
      <div className="flex items-center justify-between rounded-lg border border-border/50 p-4">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-primary/10 p-2">
            {push.isSubscribed ? (
              <Bell className="h-4 w-4 text-primary" />
            ) : (
              <BellOff className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
          <div className="space-y-1">
            <Label htmlFor="push-notifications" className="text-sm font-medium cursor-pointer">
              Push Notifications (This Device)
            </Label>
            <p className="text-xs text-muted-foreground">
              Enable push notifications on this device.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {push.isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          <Switch
            id="push-notifications"
            checked={push.isSubscribed}
            onCheckedChange={handleDeviceToggle}
            disabled={push.isLoading}
          />
        </div>
      </div>

      {/* Notification Categories */}
      {push.isSubscribed && prefs.categories.length > 0 && (
        <Collapsible open={categoriesOpen} onOpenChange={setCategoriesOpen}>
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex w-full items-center justify-between rounded-lg border border-border/50 p-4 text-left hover:bg-muted/30 transition-colors"
            >
              <div className="space-y-1">
                <p className="text-sm font-medium">Notification Types</p>
                <p className="text-xs text-muted-foreground">
                  Choose which types of notifications you receive
                </p>
              </div>
              <ChevronDown
                className={cn(
                  'h-4 w-4 text-muted-foreground transition-transform',
                  categoriesOpen && 'rotate-180'
                )}
              />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-2">
            <div className="space-y-1 rounded-lg border border-border/50 divide-y divide-border/50">
              {prefs.categories.map((category) => {
                const isEnabled = prefs.preferences?.categories?.[category.key] ?? true;
                return (
                  <div
                    key={category.key}
                    className="flex items-center justify-between p-3"
                  >
                    <div className="space-y-0.5 pr-4">
                      <Label
                        htmlFor={`category-${category.key}`}
                        className="text-sm font-medium cursor-pointer"
                      >
                        {category.label}
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        {category.description}
                      </p>
                    </div>
                    <Switch
                      id={`category-${category.key}`}
                      checked={isEnabled}
                      onCheckedChange={(checked) => handleCategoryToggle(category.key, checked)}
                      disabled={prefs.isLoading}
                    />
                  </div>
                );
              })}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Mobile info */}
      <div className="flex items-start gap-3 rounded-lg border border-border/50 bg-muted/30 p-4">
        <Smartphone className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
        <div className="space-y-1">
          <p className="text-sm font-medium">Mobile & Desktop Support</p>
          <p className="text-xs text-muted-foreground">
            Push notifications work on Android, Windows, macOS, and iOS (Safari 16.4+ with "Add to Home Screen").
          </p>
        </div>
      </div>

      {/* Status indicator */}
      {push.isSubscribed && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="h-2 w-2 rounded-full bg-success" />
          <span>Push notifications are enabled for this device</span>
        </div>
      )}
    </div>
  );
}

export default function SettingsDialog({ open, onOpenChange, defaultTab = 'appearance' }: SettingsDialogProps) {
  const { settings, setSettings } = useSettings();
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'appearance' | 'notifications'>(defaultTab);
  const [completeSave, cancelSave] = useDebouncedCallback(() => {
    setIsSaving(false);
    onOpenChange(false);
  }, 300, [onOpenChange]);

  // Update active tab when defaultTab changes or dialog opens
  useEffect(() => {
    if (open) {
      setActiveTab(defaultTab);
    }
  }, [open, defaultTab]);

  const handleThemeChange = useCallback((theme: 'system' | 'light' | 'dark') => {
    setSettings((prev) => ({ ...prev, theme }));
  }, [setSettings]);

  const handleDensityChange = useCallback((density: 'comfortable' | 'compact') => {
    setSettings((prev) => ({ ...prev, density }));
  }, [setSettings]);

  const handleDevToolsToggle = useCallback((enabled: boolean) => {
    setSettings((prev) => ({ ...prev, enableDevTools: enabled }));
  }, [setSettings]);

  const handleSave = useCallback(() => {
    setIsSaving(true);
    completeSave();
  }, [completeSave]);

  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  useEffect(() => cancelSave, [cancelSave]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]" aria-describedby="settings-dialog-description">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-muted-foreground" />
            <DialogTitle>Settings</DialogTitle>
          </div>
          <DialogDescription id="settings-dialog-description">
            Configure application settings and preferences.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(90vh-200px)] pr-4">
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'appearance' | 'notifications')} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="appearance">Appearance</TabsTrigger>
              <TabsTrigger value="notifications">Notifications</TabsTrigger>
            </TabsList>

            <TabsContent value="appearance" className="space-y-4 mt-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="theme">Theme</Label>
                  <Select value={settings.theme} onValueChange={handleThemeChange}>
                    <SelectTrigger id="theme">
                      <SelectValue placeholder="Select theme" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="system">System</SelectItem>
                      <SelectItem value="light">Light</SelectItem>
                      <SelectItem value="dark">Dark</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Choose your preferred color scheme. System uses your OS preference.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="density">Density</Label>
                  <Select value={settings.density} onValueChange={handleDensityChange}>
                    <SelectTrigger id="density">
                      <SelectValue placeholder="Select density" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="comfortable">Comfortable</SelectItem>
                      <SelectItem value="compact">Compact</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Adjust spacing and sizing of interface elements.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dev-tools">Developer Tools</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="dev-tools"
                      checked={settings.enableDevTools}
                      onChange={(e) => handleDevToolsToggle(e.target.checked)}
                      className="h-4 w-4 rounded border-border bg-background text-primary focus:ring-ring"
                    />
                    <Label htmlFor="dev-tools" className="font-normal cursor-pointer">
                      Enable developer tools
                    </Label>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Show developer tools and diagnostic features.
                  </p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="notifications" className="space-y-4 mt-4">
              <PushNotificationSettings />
            </TabsContent>
          </Tabs>
        </ScrollArea>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={isSaving}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
